import { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import { useCompany } from '@/hooks/useCompany';
import { useAuth } from '@/context/AuthContext';
import { userManagementApiClient, CompanyUser, Invitation } from '@/clients/userManagementApiClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, UserPlus, Users, Shield, User as UserIcon } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES, type Locale } from '@financialharmony/i18n';
import { useFormatters } from '@financialharmony/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// "Same as mine" sends no language: the invitee gets the inviter's own saved language, English if none.
type InviteLanguage = 'inherit' | Locale;

export default function UserManagement() {
  const t = useTranslations('userManagement');
  const tc = useTranslations('common.actions');
  const { formatDate } = useFormatters();
  const { company } = useCompany();
  const { platformRole } = useAuth();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteeType, setInviteeType] = useState<'client' | 'consultant'>('client');
  const [inviteLanguage, setInviteLanguage] = useState<InviteLanguage>('inherit');
  // Staff's explicit Admin/User pick; null until they touch the select, so the default can follow
  // whether the company already has an admin (Admin preselected when it has none).
  const [pickedInviteRole, setPickedInviteRole] = useState<'admin' | 'user' | null>(null);
  // False until the user list has loaded successfully: an unknown admin state never pre-selects Admin.
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  // FIN-1167 EC7: the last invitee who will see a tax account that is also its owner's personal one.
  const [sharedTaxAccountInvitee, setSharedTaxAccountInvitee] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const [userToRemove, setUserToRemove] = useState<CompanyUser | null>(null);
  const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null);

  useEffect(() => {
    if (company?.id) {
      loadData();
      checkUserRole();
    }
  }, [company?.id]);

  const checkUserRole = async () => {
    if (!company?.id) return;

    const result = await userManagementApiClient.checkUserRole(company.id);
    if (result.success && result.data) {
      setUserRole(result.data);
    }
  };

  const loadData = async () => {
    if (!company?.id) return;

    setIsLoading(true);
    try {
      const [usersResult, invitationsResult] = await Promise.all([
        userManagementApiClient.listUsers(company.id),
        userManagementApiClient.listInvitations(company.id),
      ]);

      if (usersResult.success && usersResult.data) {
        setUsers(usersResult.data);
        setUsersLoaded(true);
      } else {
        toast.error(usersResult.error || t('toasts.loadUsersFailed'));
      }

      if (invitationsResult.success && invitationsResult.data) {
        setInvitations(invitationsResult.data);
      } else {
        toast.error(invitationsResult.error || t('toasts.loadInvitationsFailed'));
      }
    } catch (error) {
      toast.error(t('toasts.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const companyHasAdmin = users.some((u) => u.role === 'admin');
  const inviteRole: 'admin' | 'user' =
    pickedInviteRole ?? (usersLoaded && !companyHasAdmin && inviteeType === 'client' ? 'admin' : 'user');

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company?.id || !inviteEmail) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error(t('toasts.invalidEmail'));
      return;
    }

    setIsInviting(true);
    try {
      const redirectBaseUrl = inviteeType === 'client'
        ? (import.meta.env.VITE_CLIENT_SITE_URL ?? 'http://localhost:3000')
        : window.location.origin;
      const result = await userManagementApiClient.inviteUser(
        company.id, inviteEmail, inviteRole, redirectBaseUrl, inviteeType,
        inviteLanguage === 'inherit' ? undefined : inviteLanguage
      );

      if (result.success) {
        if (result.data?.addedExistingUser) {
          toast.success(t('toasts.addedExisting', { email: inviteEmail }));
        } else {
          toast.success(t('toasts.invited', { email: inviteEmail }));
        }
        setSharedTaxAccountInvitee(result.data?.sharedPersonalTaxAccount ? inviteEmail : null);
        setInviteEmail('');
        setPickedInviteRole(null);
        await loadData(); // Reload to show the new invitation
      } else {
        toast.error(result.error || t('toasts.inviteFailed'));
      }
    } catch (error) {
      toast.error(t('toasts.inviteFailed'));
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!company?.id || !userToRemove) return;

    try {
      const result = await userManagementApiClient.removeUser(company.id, userToRemove.userId);

      if (result.success) {
        toast.success(t('toasts.removed'));
        await loadData();
      } else {
        toast.error(result.error || t('toasts.removeFailed'));
      }
    } catch (error) {
      toast.error(t('toasts.removeFailed'));
    } finally {
      setUserToRemove(null);
    }
  };

  const handleRevokeInvitation = async () => {
    if (!invitationToRevoke) return;

    try {
      const result = await userManagementApiClient.revokeInvitation(invitationToRevoke.id);

      if (result.success) {
        toast.success(t('toasts.revoked'));
        await loadData();
      } else {
        toast.error(result.error || t('toasts.revokeFailed'));
      }
    } catch (error) {
      toast.error(t('toasts.revokeFailed'));
    } finally {
      setInvitationToRevoke(null);
    }
  };

  if (userRole !== 'admin' && platformRole !== 'platform_admin') {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight">{t('page.title')}</h1>
        <Alert>
          <AlertDescription>{t('page.noAccess')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">{t('page.title')}</h1>
      </div>

      {/* Invite User Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {t('invite.title')}
          </CardTitle>
          <CardDescription>
            {t('invite.description')}
            {usersLoaded && !companyHasAdmin && ` ${t('invite.noAdminHint')}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInviteUser} className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="email" className="sr-only">{t('invite.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('invite.emailPlaceholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={isInviting}
              />
            </div>
            <Select
              value={inviteeType}
              onValueChange={(v) => setInviteeType(v as 'client' | 'consultant')}
              disabled={isInviting}
            >
              <SelectTrigger className="w-36" aria-label={t('invite.typeLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="client">{t('invite.client')}</SelectItem>
                <SelectItem value="consultant">{t('invite.consultant')}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={inviteLanguage}
              onValueChange={(v) => setInviteLanguage(v as InviteLanguage)}
              disabled={isInviting}
            >
              <SelectTrigger className="w-40" aria-label={t('invite.languageLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">{t('invite.sameAsMine')}</SelectItem>
                {SUPPORTED_LOCALES.map((locale) => (
                  <SelectItem key={locale} value={locale}>{LOCALE_NATIVE_NAMES[locale]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={inviteRole}
              onValueChange={(v) => setPickedInviteRole(v as 'admin' | 'user')}
              disabled={isInviting}
            >
              <SelectTrigger className="w-32" aria-label={t('invite.roleLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">{t('invite.admin')}</SelectItem>
                <SelectItem value="user">{t('invite.user')}</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" disabled={isInviting || !inviteEmail}>
              {isInviting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('invite.sending')}
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  {t('invite.send')}
                </>
              )}
            </Button>
          </form>
          {sharedTaxAccountInvitee && (
            <Alert className="mt-4" data-testid="invite-shared-tax-account-warning">
              <AlertDescription>
                {t('invite.sharedTaxAccount', { email: sharedTaxAccountInvitee })}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Company Users Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('users.title', { count: String(users.length) })}
          </CardTitle>
          <CardDescription>
            {t('users.description', { company: company?.name ?? '' })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('users.empty')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.user')}</TableHead>
                  <TableHead>{t('users.email')}</TableHead>
                  <TableHead>{t('users.role')}</TableHead>
                  <TableHead>{t('users.added')}</TableHead>
                  <TableHead className="text-right">{t('users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.userId}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.role === 'admin' ? (
                          <Shield className="h-4 w-4 text-primary" />
                        ) : (
                          <UserIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                        {user.firstName || user.lastName
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : t('users.noName')}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {t(`roles.${user.role}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(user.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setUserToRemove(user)}
                        disabled={user.role === 'admin' && users.filter(u => u.role === 'admin').length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations Section */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t('invitations.title', { count: String(invitations.length) })}
            </CardTitle>
            <CardDescription>
              {t('invitations.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('users.email')}</TableHead>
                  <TableHead>{t('users.role')}</TableHead>
                  <TableHead>{t('invitations.invitedBy')}</TableHead>
                  <TableHead>{t('invitations.sent')}</TableHead>
                  <TableHead>{t('invitations.expires')}</TableHead>
                  <TableHead className="text-right">{t('users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{t(`roles.${invitation.role}`)}</Badge>
                    </TableCell>
                    <TableCell>{invitation.invitedByEmail || t('invitations.unknown')}</TableCell>
                    <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                    <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setInvitationToRevoke(invitation)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Remove User Confirmation Dialog */}
      <AlertDialog open={!!userToRemove} onOpenChange={() => setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('removeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('removeDialog.body', { email: userToRemove?.email ?? '', company: company?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveUser} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('removeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invitation Confirmation Dialog */}
      <AlertDialog open={!!invitationToRevoke} onOpenChange={() => setInvitationToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('revokeDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('revokeDialog.body', { email: invitationToRevoke?.email ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeInvitation}>
              {t('revokeDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
